import React, { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import { Form as FormLayout, Modal } from "react-bootstrap";
import * as Yup from "yup";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import {
    errorMessage,
    formatAmount,
    formatDate,
    formatStatus,
    formatUsername,
    formatActivity,
    permissionCssClass,
    truncateString,
    ucFirst,
} from "../../../util/Helpers";
import cogoToast from "cogo-toast";
import { Permissions, PermissionType } from "../../../util/Permissions";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import PayoutDividendsModal from "./modals/PayoutDividendsModal";
import * as handlers from "../../Instrument/_redux/selected-instrument/selectedInstrumentHandlers";
import DynamicSimpleTable from "../../../util/DynamicSimpleReactTable";
import { SubHeader } from "../../Common/layout/SubHeader";
import { Redirect, Switch } from "react-router-dom";
import { ContentRoute } from "../../../../_metronic/layout";
import { SelectiveDividendPayout } from "./SelectiveDividendPayout";
import { BonusShares } from "./BonusShares";
import TextInput from "../../Common/components/text-input";
import DateInput from "../../Common/components/date-input";
import SelectInput from "../../Common/components/select-input";
import {
    useApproveDividendBatchMutation,
    useCreateDividendAnnouncementMutation,
    useExecuteDividendBatchMutation,
    useGenerateDividendBatchMutation,
    useGetDividendAnnouncementsQuery,
    useGetDividendBatchAuditQuery,
    useGetDividendBatchQuery,
    useGetDividendBatchesQuery,
    useGetEligibleSharesQuery,
    useRecallDividendBatchMutation,
    useUpdateDividendBatchRecordMutation
} from "../../../../redux/apiSlice";

export const HoldingManagement = (
    {
        match: {
            params: { instrumentId },
        },
    }
) => {
    const adminUser = useSelector(state => state.auth.user);
    const selectedInstrument = useSelector(({ selectedInstrument }) => {
        const data = selectedInstrument?.data?.id === instrumentId ? selectedInstrument.data : null;
        return { ...selectedInstrument, data };
    }, shallowEqual);

    // Redux dispatch
    const dispatch = useDispatch();

    /* Fetch Instrument Details */
    useEffect(() => {
        dispatch(handlers.fetchInstrumentDetails(instrumentId));
    }, [dispatch, instrumentId]);

    /* Holdings Modal State Management */
    const [holdingsModal, setHoldingsModal] = useState({ showPayoutDividendsModal: false, details: null });
    const [qualificationDate, setQualificationDate] = useState("");
    const [dividendPerShare, setDividendPerShare] = useState("");
    const [eligibleAccountId, setEligibleAccountId] = useState("");
    const [eligibleUserId, setEligibleUserId] = useState("");
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [adjustmentModal, setAdjustmentModal] = useState({ show: false, record: null });
    const [batchModal, setBatchModal] = useState({ show: false, announcement: null });
    const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
    const [confirmBatchAction, setConfirmBatchAction] = useState({ show: false, action: null });
    const [auditModal, setAuditModal] = useState({ show: false, batchId: null, referenceId: null });
    const [batchesPage, setBatchesPage] = useState(1);
    const [batchesSize, setBatchesSize] = useState(20);
    const [announcementsPage, setAnnouncementsPage] = useState(1);
    const [announcementsSize, setAnnouncementsSize] = useState(20);
    const [batchStatusFilter, setBatchStatusFilter] = useState("ALL");

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setHoldingsModal({ ...holdingsModal, [modal]: !holdingsModal[modal] })
    }

    const { data: eligibleSharesResponse, isFetching: isEligibleLoading } = useGetEligibleSharesQuery(
        { instrumentId, qualificationDate, accountId: eligibleAccountId || undefined, userId: eligibleUserId || undefined },
        { skip: !qualificationDate }
    );
    const { data: dividendAnnouncementsResponse, isFetching: isAnnouncementsLoading } = useGetDividendAnnouncementsQuery({ page: announcementsPage, size: announcementsSize });
    const { data: dividendBatchesResponse, isFetching: isBatchesLoading } = useGetDividendBatchesQuery({ page: batchesPage, size: batchesSize });
    const { data: dividendBatchResponse, isFetching: isBatchLoading } = useGetDividendBatchQuery(selectedBatchId, { skip: !selectedBatchId });
    const { data: dividendBatchAuditResponse } = useGetDividendBatchAuditQuery(auditModal.batchId, { skip: !auditModal.batchId });

    const [createDividendAnnouncement, { isLoading: isCreatingAnnouncement }] = useCreateDividendAnnouncementMutation();
    const [generateDividendBatch, { isLoading: isGeneratingBatch }] = useGenerateDividendBatchMutation();
    const [updateDividendBatchRecord, { isLoading: isUpdatingBatchRecord }] = useUpdateDividendBatchRecordMutation();
    const [approveDividendBatch, { isLoading: isApprovingBatch }] = useApproveDividendBatchMutation();
    const [executeDividendBatch, { isLoading: isExecutingBatch }] = useExecuteDividendBatchMutation();
    const [recallDividendBatch, { isLoading: isRecallingBatch }] = useRecallDividendBatchMutation();

    const openAdjustmentModal = record => () => setAdjustmentModal({ show: true, record });
    const closeAdjustmentModal = () => setAdjustmentModal({ show: false, record: null });
    const openBatchModal = announcement => () => setBatchModal({ show: true, announcement });
    const closeBatchModal = () => setBatchModal({ show: false, announcement: null });
    const openAnnouncementModal = () => setAnnouncementModalOpen(true);
    const closeAnnouncementModal = () => setAnnouncementModalOpen(false);
    const openBatchActionModal = action => () => setConfirmBatchAction({ show: true, action });
    const closeBatchActionModal = () => setConfirmBatchAction({ show: false, action: null });
    const openAuditModal = batch => () => setAuditModal({ show: true, batchId: getBatchId(batch), referenceId: getReferenceId(batch) });
    const closeAuditModal = () => setAuditModal({ show: false, batchId: null, referenceId: null });

    const normalizeArray = (data) => {
        if (Array.isArray(data)) {
            return data;
        }
        if (Array.isArray(data?.data)) {
            return data.data;
        }
        if (data?.data && typeof data.data === "object") {
            return [data.data];
        }
        if (Array.isArray(data?.records)) {
            return data.records;
        }
        if (Array.isArray(data?.items)) {
            return data.items;
        }
        return [];
    };

    const eligibleShares = normalizeArray(eligibleSharesResponse);
    const announcements = normalizeArray(dividendAnnouncementsResponse);
    const batches = normalizeArray(dividendBatchesResponse);
    const batchPayload = dividendBatchResponse?.data || dividendBatchResponse || {};
    const batchDetails = batchPayload?.batch || batchPayload || {};
    const matchedRecords = normalizeArray(batchPayload?.matched);
    const mismatchedRecords = normalizeArray(batchPayload?.mismatched);
    const batchRecords = matchedRecords.length + mismatchedRecords.length > 0
        ? [...matchedRecords, ...mismatchedRecords]
        : Array.isArray(batchDetails?.records)
            ? batchDetails.records
            : Array.isArray(batchDetails?.data)
                ? batchDetails.data
                : Array.isArray(batchDetails?.items)
                    ? batchDetails.items
                    : [];
    const auditRecords = normalizeArray(dividendBatchAuditResponse);
    const batchesTotal = dividendBatchesResponse?.total || dividendBatchesResponse?.totalCount || dividendBatchesResponse?.count || dividendBatchesResponse?.data?.total || dividendBatchesResponse?.data?.totalCount || null;
    const announcementsTotal = dividendAnnouncementsResponse?.total || dividendAnnouncementsResponse?.totalCount || dividendAnnouncementsResponse?.count || dividendAnnouncementsResponse?.data?.total || dividendAnnouncementsResponse?.data?.totalCount || null;

    const hasInstrumentFilter = batches.some(batch => batch?.instrumentId || batch?.assetId || batch?.instrument?.id || batch?.announcement?.instrumentId);
    const filteredBatches = hasInstrumentFilter
        ? batches.filter(batch => {
            const batchInstrumentId = batch?.instrumentId || batch?.assetId || batch?.instrument?.id || batch?.announcement?.instrumentId;
            return batchInstrumentId ? batchInstrumentId === instrumentId : true;
        })
        : batches;
    const hasAnnouncementInstrumentFilter = announcements.some(announcement => announcement?.instrumentId || announcement?.assetId || announcement?.instrument?.id);
    const filteredAnnouncements = hasAnnouncementInstrumentFilter
        ? announcements.filter(announcement => {
            const announcementInstrumentId = announcement?.instrumentId || announcement?.assetId || announcement?.instrument?.id;
            return announcementInstrumentId ? announcementInstrumentId === instrumentId : true;
        })
        : announcements;

    const getRecordId = record => record?.recordId || record?.id || record?.autoId;
    const getBatchId = batch => batch?.batchId || batch?.id || batch?.autoId;
    const getAnnouncementId = announcement => announcement?.announcementId || announcement?.id || announcement?.autoId;
    const getReferenceId = entry => entry?.referenceId || entry?.ref || entry?.batchReferenceId || entry?.autoId || entry?.id;
    const getQualificationDate = entry => entry?.qualificationDate || entry?.announcement?.qualificationDate || entry?.qualification_date;
    const getPayoutStatus = entry => entry?.status || entry?.payoutStatus || entry?.state;
    const getInstrumentSymbol = entry => entry?.symbol || entry?.instrumentSymbol || entry?.instrument?.symbol || selectedInstrument?.data?.symbol || "";
    const getDividendUserId = entry => entry?.userId || entry?.user || entry?.user_id || entry?.user?.id || entry?.user?.userId;
    const getDividendAccountId = entry => entry?.accountId || entry?.account || entry?.account_id || entry?.userAccountId;
    const getSharesValue = entry => entry?.shares || entry?.shareQuantity || entry?.sharesHeld || entry?.eligibleShares || entry?.quantity || 0;
    const getDividendPerShareValue = entry => entry?.dividendPerShare || entry?.dividendPerShareAmount || entry?.amountPerShare || dividendPerShare || 0;
    const getDividendAmountValue = entry => entry?.dividendAmount || entry?.netDividendAmount || entry?.amount || entry?.payoutAmount || entry?.totalDividendAmount || 0;
    const getExpectedDividendAmount = entry => entry?.expectedDividendAmount || entry?.expectedAmount || entry?.expectedDividend || entry?.expected || entry?.grossDividendAmount || 0;
    const batchStatusOptions = ["ALL", "PENDING", "APPROVED", "EXECUTED", "FAILED"];
    const formatAuditAction = action => action ? ucFirst(action.replace(/_/g, " ").toLowerCase()) : "-";
    const getAuditUserLabel = (entry) => {
        const name = entry?.username || entry?.userName || entry?.user;
        const id = entry?.userId || entry?.user;
        if (name && id && name !== id) {
            return `${name} (${id})`;
        }
        return name || id || "-";
    };
    const auditFieldOrder = [
        "userId",
        "accountId",
        "dividendPerShare",
        "eligibleShares",
        "grossDividendAmount",
        "netDividendAmount",
        "status",
        "qualificationFlag",
        "isManuallyAdjusted",
        "withholdingTax",
        "userGroup",
    ];
    const parseAuditValue = (value) => {
        if (typeof value !== "string") {
            return value;
        }
        const trimmed = value.trim();
        if (!trimmed) {
            return value;
        }
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
            try {
                return JSON.parse(trimmed);
            } catch (error) {
                return value;
            }
        }
        return value;
    };
    const formatAuditFieldValue = (key, value) => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }
        if (key.toLowerCase().includes("amount") || key.toLowerCase().includes("share")) {
            return Number.isFinite(Number(value))
                ? formatAmount(batchCurrency, Number(value))
                : String(value);
        }
        if (typeof value === "boolean") {
            return value ? "Yes" : "No";
        }
        return String(value);
    };
    const renderAuditValue = (value) => {
        if (value === null || value === undefined || value === "") {
            return <div className="text-muted">-</div>;
        }
        const parsed = parseAuditValue(value);
        if (parsed && typeof parsed === "object") {
            const entries = auditFieldOrder
                .filter((key) => parsed[key] !== undefined)
                .map((key) => [key, parsed[key]]);
            const fallbackEntries = entries.length === 0
                ? Object.entries(parsed).slice(0, 8)
                : entries;
            return (
                <div className="bg-light rounded p-3 mb-0">
                    <div className="row">
                        {fallbackEntries.map(([key, fieldValue]) => (
                            <div key={key} className="col-md-6 mb-2">
                                <div className="text-muted small">{ucFirst(key.replace(/_/g, " "))}</div>
                                <div className="text-monospace small">{formatAuditFieldValue(key, fieldValue)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        return <div className="text-monospace small">{String(parsed)}</div>;
    };
    const visibleBatches = batchStatusFilter === "ALL"
        ? filteredBatches
        : filteredBatches.filter(batch => {
            const status = getPayoutStatus(batch);
            return status ? status.toString().toLowerCase() === batchStatusFilter.toLowerCase() : false;
        });
    const batchTotalRecords = batchPayload?.totalRecords || batchDetails?.totalRecords || batchDetails?.recordCount || batchDetails?.totalItems || batchRecords.length;
    const batchCurrency = batchDetails?.currency || batchDetails?.announcement?.currency || batchRecords.find(record => record?.currency)?.currency || selectedInstrument?.data?.currency;
    const batchTotalAmount = batchDetails?.totalAmount || batchDetails?.totalDividendAmount || batchDetails?.amount || batchRecords.reduce((sum, record) => sum + Number(getDividendAmountValue(record) || 0), 0);
    const batchStatusValue = getPayoutStatus(batchDetails);
    const batchStatusLower = batchStatusValue ? batchStatusValue.toString().toLowerCase() : "";
    const isBatchApproved = batchStatusLower === "approved";
    const isBatchExecuted = batchStatusLower === "executed";
    const hasServerRecordSplit = matchedRecords.length + mismatchedRecords.length > 0;
    const matchedEntries = hasServerRecordSplit ? matchedRecords : batchRecords;
    const mismatchedEntries = hasServerRecordSplit ? mismatchedRecords : [];
    const batchesHasMore = batchesTotal ? batchesPage * batchesSize < batchesTotal : visibleBatches.length === batchesSize;
    const announcementsHasMore = announcementsTotal ? announcementsPage * announcementsSize < announcementsTotal : filteredAnnouncements.length === announcementsSize;

    return (
        <div className="d-flex flex-column h-100">
            <SubHeader>
                <SubHeader.Nav>
                    <SubHeader.NavItem label={"Positions"} to={`/holdings/${instrumentId}/positions`} />
                    <SubHeader.NavItem label={"Dividend History"} to={`/holdings/${instrumentId}/dividend-history`} />
                    <SubHeader.NavItem label={"Dividend Announcements"} to={`/holdings/${instrumentId}/dividend-announcements`} />
                    <SubHeader.NavItem label={"Dividend Batches"} to={`/holdings/${instrumentId}/dividend-batches`} />
                    <SubHeader.NavItem label={"Selective Dividend Payout"} to={`/holdings/${instrumentId}/selective-payout`} />
                    <SubHeader.NavItem label={"Bonus Shares"} to={`/holdings/${instrumentId}/bonus-shares`} />

                </SubHeader.Nav>
            </SubHeader>
            <Switch>
                <Redirect exact={true} from="/holdings/:instrumentId" to="/holdings/:instrumentId/positions" />
                <Redirect exact={true} from="/holdings/:instrumentId/dividends" to="/holdings/:instrumentId/dividend-batches" />

                <ContentRoute path="/holdings/:instrumentId/positions" permission={Permissions.HOLDINGS}>
                    <div className="py-4 px-6 h-100">
                        <Card className="h-100" style={{ height: "100%" }}>
                            <CardHeader title={`Positions`}>
                                <CardHeaderToolbar>
                                    <button
                                        type="button"
                                        className={`btn btn-primary mr-4 ${permissionCssClass(adminUser, Permissions.HOLDINGS, PermissionType.CREATE)}`}
                                        onClick={toggleModal("showPayoutDividendsModal")}
                                    >
                                        Payout Dividends
                                    </button>
                                </CardHeaderToolbar>
                            </CardHeader>
                            <CardBody>
                                <DynamicSimpleTable
                                    key={`${instrumentId}-positions`}
                                    searchBox={false}
                                    queryParams={`instrumentId=${instrumentId}`}
                                    source={{ url: `/positions` }}
                                    columns={[
                                        { title: "USER", format: row => formatUsername(row.userId, row.username, null, row.accountId, "positions") },
                                        { title: "USER GROUP", key: "userGroup" },
                                        { title: "SYMBOL", format: row => row.asset?.symbol },
                                        { title: "QUANTITY", key: "totalQuantity" },
                                        { title: "CURRENCY", format: row => row.asset?.currency },
                                        { title: "CREATED AT", format: row => formatDate(row.created) },
                                        { title: "UPDATED AT", format: row => formatDate(row.updated) },
                                    ]}
                                    onError={(err) =>
                                        cogoToast.error(errorMessage(err))
                                    }
                                    refreshIcon={true}
                                />
                            </CardBody>
                        </Card>
                    </div>
                </ContentRoute>
                <ContentRoute path="/holdings/:instrumentId/dividend-batches" exact permission={Permissions.HOLDINGS}>
                    <div className="py-4 px-6 h-100">
                        <Card className="mb-6">
                            <CardHeader title={`Dividend Batches`}>
                                <CardHeaderToolbar>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex align-items-center mr-4">
                                            <span className="text-muted mr-2">Status</span>
                                            <select
                                                className="form-control form-control-sm"
                                                value={batchStatusFilter}
                                                onChange={(event) => {
                                                    setBatchStatusFilter(event.target.value);
                                                    setBatchesPage(1);
                                                }}
                                            >
                                                {batchStatusOptions.map((status) => (
                                                    <option key={status} value={status}>
                                                        {status === "ALL" ? "All" : ucFirst(status.toLowerCase())}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <button
                                            type="button"
                                            className={`btn btn-sm btn-primary ${permissionCssClass(adminUser, Permissions.HOLDINGS, PermissionType.CREATE)}`}
                                            onClick={() => setBatchModal({ show: true, announcement: null })}
                                        >
                                            Generate Batch
                                        </button>
                                    </div>
                                </CardHeaderToolbar>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <table className="table table-sm mb-0" style={{ borderCollapse: "separate", borderSpacing: "0 8px" }}>
                                        <thead>
                                            <tr>
                                                <th>REFERENCE ID</th>
                                                <th>ANNOUNCEMENT ID</th>
                                                <th>INSTRUMENT</th>
                                                <th>QUALIFICATION DATE</th>
                                                <th>DIVIDEND PER SHARE</th>
                                                <th>TOTAL ELIGIBLE</th>
                                                <th>TOTAL AMOUNT</th>
                                                <th>STATUS</th>
                                                <th>CREATED</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isBatchesLoading && (
                                                <tr>
                                                    <td colSpan="10">Loading...</td>
                                                </tr>
                                            )}
                                            {!isBatchesLoading && visibleBatches.length === 0 && (
                                                <tr>
                                                    <td colSpan="10">No dividend batches available.</td>
                                                </tr>
                                            )}
                                            {visibleBatches.map((batch) => {
                                                const batchId = getBatchId(batch);
                                                return (
                                                    <tr key={batchId || getReferenceId(batch)}>
                                                        <td className="text-monospace">{getReferenceId(batch)}</td>
                                                        <td className="text-monospace">{getAnnouncementId(batch)}</td>
                                                        <td>{getInstrumentSymbol(batch) || batch?.symbol || batch?.instrumentId}</td>
                                                        <td>{formatDate(getQualificationDate(batch))}</td>
                                                        <td>{formatAmount(batch.currency || selectedInstrument?.data?.currency, getDividendPerShareValue(batch))}</td>
                                                        <td>{batch.totalEligibleUsers ?? batch.totalUsers ?? "-"}</td>
                                                        <td>{formatAmount(batch.currency || selectedInstrument?.data?.currency, batch.totalDividendAmount ?? batch.totalAmount ?? batch.amount)}</td>
                                                        <td>{formatStatus(ucFirst(getPayoutStatus(batch)))}</td>
                                                        <td>{formatDate(batch.created || batch.createdAt || batch.updated)}</td>
                                                        <td className="text-right">
                                                            <div className="d-inline-flex align-items-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-light btn-hover-primary btn-sm mr-2"
                                                                    onClick={() => setSelectedBatchId(batchId || getReferenceId(batch))}
                                                                    title="View"
                                                                >
                                                                    <i className="fa fa-eye" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-light btn-hover-primary btn-sm"
                                                                    onClick={openAuditModal(batch)}
                                                                    title="Audit"
                                                                >
                                                                    <i className="fa fa-clipboard-list" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex flex-wrap justify-content-between align-items-center mt-4">
                                    <div className="text-muted">
                                        Page {batchesPage}{batchesTotal ? ` of ${Math.ceil(batchesTotal / batchesSize)}` : ""}
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <select
                                            className="form-control form-control-sm mr-3"
                                            value={batchesSize}
                                            onChange={(event) => {
                                                setBatchesSize(Number(event.target.value));
                                                setBatchesPage(1);
                                            }}
                                        >
                                            {[10, 20, 50].map((size) => (
                                                <option key={size} value={size}>{size} / page</option>
                                            ))}
                                        </select>
                                        <ul className="pagination pagination-sm mb-0">
                                            <li className={`page-item ${batchesPage <= 1 ? "disabled" : ""}`} style={{ marginRight: "0.5rem" }}>
                                                <span
                                                    className="page-link"
                                                    style={{ cursor: batchesPage <= 1 ? "default" : "pointer" }}
                                                    onClick={() => batchesPage > 1 && setBatchesPage((page) => Math.max(1, page - 1))}
                                                >
                                                    Prev
                                                </span>
                                            </li>
                                            <li className={`page-item ${batchesHasMore ? "" : "disabled"}`}>
                                                <span
                                                    className="page-link"
                                                    style={{ cursor: batchesHasMore ? "pointer" : "default" }}
                                                    onClick={() => batchesHasMore && setBatchesPage((page) => page + 1)}
                                                >
                                                    Next
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        {selectedBatchId && (
                            <Card className="mb-6">
                                <CardHeader title={`Payout Batch ${getReferenceId(batchDetails) || selectedBatchId}`}>
                                    <CardHeaderToolbar>
                                        <button
                                            type="button"
                                            className={`btn mr-3 ${permissionCssClass(adminUser, Permissions.HOLDINGS, PermissionType.UPDATE)} ${isBatchLoading || isApprovingBatch || isBatchApproved || isBatchExecuted || batchRecords.length === 0 || mismatchedRecords.length > 0
                                                    ? "btn-light"
                                                    : "btn-primary"
                                                }`}
                                            disabled={isBatchLoading || isApprovingBatch || isBatchApproved || isBatchExecuted || batchRecords.length === 0 || mismatchedRecords.length > 0}
                                            onClick={openBatchActionModal("approve")}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            type="button"
                                                className={`btn mr-3 ${permissionCssClass(adminUser, Permissions.HOLDINGS, PermissionType.UPDATE)} ${isBatchLoading || isExecutingBatch || !isBatchApproved || isBatchExecuted || batchRecords.length === 0 || mismatchedRecords.length > 0
                                                    ? "btn-light"
                                                    : "btn-success"
                                                }`}
                                            disabled={isBatchLoading || isExecutingBatch || !isBatchApproved || isBatchExecuted || batchRecords.length === 0 || mismatchedRecords.length > 0}
                                            onClick={openBatchActionModal("execute")}
                                        >
                                            Execute
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn ${permissionCssClass(adminUser, Permissions.HOLDINGS, PermissionType.UPDATE)} ${isBatchLoading || isRecallingBatch || !isBatchExecuted ? "btn-light" : "btn-danger"
                                                }`}
                                            disabled={isBatchLoading || isRecallingBatch || !isBatchExecuted}
                                            onClick={openBatchActionModal("recall")}
                                        >
                                            Recall
                                        </button>
                                    </CardHeaderToolbar>
                                </CardHeader>
                                <CardBody>
                                    {mismatchedRecords.length > 0 && (
                                        <div className="alert alert-warning">
                                            Mismatched records detected. Resolve mismatches before approval or execution.
                                        </div>
                                    )}
                                    <div className="mb-5">
                                        <div className="d-flex flex-wrap">
                                            <div className="mr-6 mb-2">
                                                <strong>Status:</strong> {formatStatus(ucFirst(getPayoutStatus(batchDetails)))}
                                            </div>
                                            <div className="mr-6 mb-2">
                                                <strong>Reference:</strong> {getReferenceId(batchDetails)}
                                            </div>
                                            <div className="mr-6 mb-2">
                                                <strong>Qualification Date:</strong> {formatDate(getQualificationDate(batchDetails))}
                                            </div>
                                            <div className="mr-6 mb-2">
                                                <strong>Total Records:</strong> {batchTotalRecords}
                                            </div>
                                            <div className="mr-6 mb-2">
                                                <strong>Matched:</strong> {batchPayload?.totalMatched ?? "-"}
                                            </div>
                                            <div className="mr-6 mb-2">
                                                <strong>Mismatched:</strong> {batchPayload?.totalMismatched ?? "-"}
                                            </div>
                                            <div className="mr-6 mb-2">
                                                <strong>Total Amount:</strong> {formatAmount(batchCurrency, batchTotalAmount)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-6">
                                        <h5 className="mb-3">Matched Entries</h5>
                                        <div className="table-responsive">
                                            <table className="table table-sm mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>USER</th>
                                                        <th>ELIGIBLE SHARES</th>
                                                        <th>DIVIDEND PER SHARE</th>
                                                        <th>GROSS AMOUNT</th>
                                                        <th>NET AMOUNT</th>
                                                        <th>QUALIFIED</th>
                                                        <th>STATUS</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {matchedEntries.map((record) => (
                                                        <tr key={getRecordId(record)}>
                                                            <td>{formatUsername(getDividendUserId(record), record.username, null, getDividendAccountId(record), "positions")}</td>
                                                            <td>{getSharesValue(record)}</td>
                                                            <td>{formatAmount(record.currency || selectedInstrument?.data?.currency, getDividendPerShareValue(record))}</td>
                                                            <td>{formatAmount(record.currency || selectedInstrument?.data?.currency, getExpectedDividendAmount(record))}</td>
                                                            <td>{formatAmount(record.currency || selectedInstrument?.data?.currency, getDividendAmountValue(record))}</td>
                                                            <td>{record.qualificationFlag === true ? "Yes" : record.qualificationFlag === false ? "No" : "-"}</td>
                                                            <td>{formatStatus(ucFirst(record.status || record.state))}</td>
                                                            <td className="text-right">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-light"
                                                                    onClick={openAdjustmentModal(record)}
                                                                    title="Adjust"
                                                                >
                                                                    <i className="fa fa-pen" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {matchedEntries.length === 0 && (
                                                        <tr>
                                                            <td colSpan="9">No matched entries.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    {mismatchedEntries.length > 0 && (
                                        <div>
                                            <h5 className="mb-3">Mismatched Entries</h5>
                                            <div className="table-responsive">
                                                <table className="table table-sm mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>USER</th>
                                                            <th>ELIGIBLE SHARES</th>
                                                            <th>DIVIDEND PER SHARE</th>
                                                            <th>GROSS AMOUNT</th>
                                                            <th>NET AMOUNT</th>
                                                            <th>QUALIFIED</th>
                                                            <th>STATUS</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {mismatchedEntries.map((record) => (
                                                            <tr key={getRecordId(record)}>
                                                                <td>{formatUsername(getDividendUserId(record), record.username, null, getDividendAccountId(record), "positions")}</td>
                                                                <td>{getSharesValue(record)}</td>
                                                                <td>{formatAmount(record.currency || selectedInstrument?.data?.currency, getDividendPerShareValue(record))}</td>
                                                                <td>{formatAmount(record.currency || selectedInstrument?.data?.currency, getExpectedDividendAmount(record))}</td>
                                                                <td>{formatAmount(record.currency || selectedInstrument?.data?.currency, getDividendAmountValue(record))}</td>
                                                                <td>{record.qualificationFlag === true ? "Yes" : record.qualificationFlag === false ? "No" : "-"}</td>
                                                                <td>{formatStatus(ucFirst(record.status || record.state))}</td>
                                                                <td className="text-right">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-light"
                                                                        onClick={openAdjustmentModal(record)}
                                                                        title="Adjust"
                                                                    >
                                                                        <i className="fa fa-pen" />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        )}
                    </div>
                </ContentRoute>
                <ContentRoute path="/holdings/:instrumentId/dividend-preview" exact permission={Permissions.HOLDINGS}>
                    <div className="py-4 px-6 h-100">
                        <Card className="mb-6">
                            <CardHeader title={`New Dividend Event Preview`} />
                            <CardBody>
                                <div className="mb-4">
                                    <div className="form-row">
                                        <div className="col">
                                            <label className="form-label font-weight-bolder">Qualification Date</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-solid"
                                                value={qualificationDate}
                                                onChange={event => setQualificationDate(event.target.value)}
                                            />
                                        </div>
                                        <div className="col">
                                            <label className="form-label font-weight-bolder">Dividend Per Share</label>
                                            <input
                                                type="number"
                                                step="any"
                                                className="form-control form-control-solid"
                                                value={dividendPerShare}
                                                onChange={event => setDividendPerShare(event.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row mt-4">
                                        <div className="col">
                                            <label className="form-label font-weight-bolder">Account ID (Optional)</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-solid"
                                                value={eligibleAccountId}
                                                onChange={event => setEligibleAccountId(event.target.value)}
                                            />
                                        </div>
                                        <div className="col">
                                            <label className="form-label font-weight-bolder">User ID (Optional)</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-solid"
                                                value={eligibleUserId}
                                                onChange={event => setEligibleUserId(event.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="text-muted mb-3">
                                    Eligible shareholders are pulled from the backend for validation before payout.
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-sm mb-0">
                                        <thead>
                                            <tr>
                                                <th>USER</th>
                                                <th>SHARES AT QUALIFICATION DATE</th>
                                                <th>DIVIDEND PER SHARE</th>
                                                <th>TOTAL DIVIDEND AMOUNT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isEligibleLoading && (
                                                <tr>
                                                    <td colSpan="4">Loading...</td>
                                                </tr>
                                            )}
                                            {!isEligibleLoading && eligibleShares.length === 0 && (
                                                <tr>
                                                    <td colSpan="4">Select a qualification date to preview eligible users.</td>
                                                </tr>
                                            )}
                                            {eligibleShares.map((entry) => {
                                                const sharesValue = Number(getSharesValue(entry));
                                                const perShareValue = Number(getDividendPerShareValue(entry));
                                                return (
                                                    <tr key={entry.userId || entry.accountId || entry.id}>
                                                        <td>{formatUsername(entry.userId, entry.username, null, entry.accountId, "positions")}</td>
                                                        <td>{sharesValue}</td>
                                                        <td>{formatAmount(entry.currency || selectedInstrument?.data?.currency, perShareValue)}</td>
                                                        <td>{formatAmount(entry.currency || selectedInstrument?.data?.currency, sharesValue * perShareValue)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex flex-wrap justify-content-between align-items-center mt-4">
                                    <div className="text-muted">
                                        Page {announcementsPage}{announcementsTotal ? ` of ${Math.ceil(announcementsTotal / announcementsSize)}` : ""}
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <select
                                            className="form-control form-control-sm mr-3"
                                            value={announcementsSize}
                                            onChange={(event) => {
                                                setAnnouncementsSize(Number(event.target.value));
                                                setAnnouncementsPage(1);
                                            }}
                                        >
                                            {[10, 20, 50].map((size) => (
                                                <option key={size} value={size}>{size} / page</option>
                                            ))}
                                        </select>
                                        <ul className="pagination pagination-sm mb-0">
                                            <li className={`page-item ${announcementsPage <= 1 ? "disabled" : ""}`} style={{ marginRight: "0.5rem" }}>
                                                <span
                                                    className="page-link"
                                                    style={{ cursor: announcementsPage <= 1 ? "default" : "pointer" }}
                                                    onClick={() => announcementsPage > 1 && setAnnouncementsPage((page) => Math.max(1, page - 1))}
                                                >
                                                    Prev
                                                </span>
                                            </li>
                                            <li className={`page-item ${announcementsHasMore ? "" : "disabled"}`}>
                                                <span
                                                    className="page-link"
                                                    style={{ cursor: announcementsHasMore ? "pointer" : "default" }}
                                                    onClick={() => announcementsHasMore && setAnnouncementsPage((page) => page + 1)}
                                                >
                                                    Next
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </ContentRoute>
                <ContentRoute path="/holdings/:instrumentId/dividend-announcements" exact permission={Permissions.HOLDINGS}>
                    <div className="py-4 px-6 h-100">
                        <Card>
                            <CardHeader title={`Dividend Announcements`}>
                                <CardHeaderToolbar>
                                    <button
                                        type="button"
                                        className={`btn btn-primary ${permissionCssClass(adminUser, Permissions.HOLDINGS, PermissionType.CREATE)}`}
                                        onClick={openAnnouncementModal}
                                    >
                                        New Announcement
                                    </button>
                                </CardHeaderToolbar>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <table className="table table-sm mb-0">
                                        <thead>
                                            <tr>
                                                <th>REFERENCE ID</th>
                                                <th>STOCK</th>
                                                <th>DIVIDEND PER SHARE</th>
                                                <th>QUALIFICATION DATE</th>
                                                <th>PAYMENT DATE</th>
                                                <th>STATUS</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isAnnouncementsLoading && (
                                                <tr>
                                                    <td colSpan="7">Loading...</td>
                                                </tr>
                                            )}
                                            {!isAnnouncementsLoading && filteredAnnouncements.length === 0 && (
                                                <tr>
                                                    <td colSpan="7">No announcements available.</td>
                                                </tr>
                                            )}
                                            {filteredAnnouncements.map((announcement) => {
                                                const announcementId = getAnnouncementId(announcement);
                                                return (
                                                    <tr key={announcementId || getReferenceId(announcement)}>
                                                        <td className="text-monospace">{announcementId}</td>
                                                        <td>{getInstrumentSymbol(announcement)}</td>
                                                        <td>{formatAmount(announcement.currency || selectedInstrument?.data?.currency, announcement.dividendPerShare || announcement.amountPerShare)}</td>
                                                        <td>{formatDate(getQualificationDate(announcement))}</td>
                                                        <td>{formatDate(announcement.paymentDate)}</td>
                                                        <td>{formatStatus(ucFirst(announcement.status || announcement.state))}</td>
                                                        <td className="text-right">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-light-primary"
                                                                onClick={openBatchModal(announcement)}
                                                                disabled={isGeneratingBatch}
                                                            >
                                                                Generate Batch
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </ContentRoute>
                <ContentRoute path="/holdings/:instrumentId/dividend-history" exact permission={Permissions.HOLDINGS}>
                    <div className="py-4 px-6 h-100">
                        <Card>
                            <CardHeader title={`Dividend History`} />
                            <CardBody>
                                <DynamicSimpleTable
                                    key={`${instrumentId}-dividends`}
                                    queryParams={
                                        `userGroup=trove`
                                        + `&limit=10`
                                        + `&liveData=true`
                                        + `&partner=sigma,zanibal,zanibal_ind`
                                        + `&comment=${selectedInstrument?.data?.symbol} dividend.`
                                    }
                                    searchBox={false}
                                    source={{ url: `/transactions` }}
                                    columns={[
                                        { title: "ACTIVITY", format: formatActivity },
                                        { title: "REFERENCE", format: row => truncateString(row.autoId, 18) },
                                        { title: "DESCRIPTION", key: "comment" },
                                        { title: "USER", format: row => formatUsername(row.userId, row.username, null, row.accountId, "transactions") },
                                        { title: "AMOUNT", format: row => formatAmount(row.currency, row.tranAmount) },
                                        { title: "STATUS", format: row => formatStatus(ucFirst(row.tranStatus)) },
                                        { title: "PARTNER", format: row => ucFirst(row.partner) },
                                        { title: "DATE", format: row => formatDate(row.created) },
                                    ]}
                                    onError={(err) =>
                                        cogoToast.error(errorMessage(err))
                                    }
                                    refreshIcon={true}
                                />
                            </CardBody>
                        </Card>
                    </div>
                </ContentRoute>

                <ContentRoute path="/holdings/:instrumentId/selective-payout" permission={Permissions.HOLDINGS}>
                    <div className="py-4 px-6 h-100">
                        <SelectiveDividendPayout
                            instrumentId={instrumentId}
                            symbol={selectedInstrument?.data?.symbol}
                        />
                    </div>
                </ContentRoute>

                <ContentRoute path="/holdings/:instrumentId/bonus-shares" permission={Permissions.HOLDINGS}>
                    <BonusShares
                        instrumentId={instrumentId}
                        symbol={selectedInstrument?.data?.symbol}
                    />
                </ContentRoute>
            </Switch>

            <PayoutDividendsModal
                {...holdingsModal}
                assetId={instrumentId}
                symbol={selectedInstrument?.data?.symbol}
                onHide={toggleModal("showPayoutDividendsModal")}
            />
            <Modal
                show={adjustmentModal.show}
                onHide={closeAdjustmentModal}
                aria-labelledby="dividend-adjustment-modal"
                backdrop={isUpdatingBatchRecord ? "static" : true}
                keyboard={!isUpdatingBatchRecord}
            >
                <Formik
                    enableReinitialize={true}
                    initialValues={{
                        dividendAmount: adjustmentModal.record ? getDividendAmountValue(adjustmentModal.record) : "",
                        adjustmentReason: ""
                    }}
                    validationSchema={Yup.object({
                        dividendAmount: Yup.number().min(0).required("Required"),
                        adjustmentReason: Yup.string().required("Required"),
                    })}
                    onSubmit={(values) => {
                        const recordId = getRecordId(adjustmentModal.record);
                        if (!recordId || !selectedBatchId) {
                            return;
                        }
                        updateDividendBatchRecord({
                            batchId: selectedBatchId,
                            recordId,
                            dividendAmount: Number(values.dividendAmount),
                            adjustmentReason: values.adjustmentReason,
                        })
                            .unwrap()
                            .then(() => {
                                cogoToast.success("Adjustment saved");
                                closeAdjustmentModal();
                            })
                            .catch(() => null);
                    }}
                >
                    {({ isValid }) => (
                        <Form>
                            <Modal.Header>
                                <Modal.Title id="dividend-adjustment-modal">
                                    Adjust Dividend Record
                                </Modal.Title>
                                <button type="button" className="close" onClick={closeAdjustmentModal}>
                                    <span aria-hidden="true" className="d-block">×</span>
                                    <span className="sr-only">Close</span>
                                </button>
                            </Modal.Header>
                            <Modal.Body className="px-10 py-2">
                                <fieldset disabled={isUpdatingBatchRecord}>
                                    <FormLayout.Row>
                                        <TextInput
                                            label="Dividend Amount"
                                            name="dividendAmount"
                                            type="number"
                                            step="any"
                                            min="0"
                                        />
                                        <TextInput
                                            label="Adjustment Reason"
                                            name="adjustmentReason"
                                            type="text"
                                        />
                                    </FormLayout.Row>
                                </fieldset>
                            </Modal.Body>
                            <Modal.Footer>
                                <button
                                    type="button"
                                    onClick={closeAdjustmentModal}
                                    className="btn btn-light btn-elevate px-4"
                                    disabled={isUpdatingBatchRecord}
                                >
                                    Close
                                </button>
                                <> </>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-elevate px-4 ml-4"
                                    disabled={isUpdatingBatchRecord || !isValid}
                                >
                                    Save Adjustment
                                </button>
                            </Modal.Footer>
                        </Form>
                    )}
                </Formik>
            </Modal>
            <Modal
                show={batchModal.show}
                onHide={closeBatchModal}
                aria-labelledby="dividend-batch-modal"
                backdrop={isGeneratingBatch ? "static" : true}
                keyboard={!isGeneratingBatch}
            >
                <Formik
                    enableReinitialize={true}
                    initialValues={{
                        announcementId: getAnnouncementId(batchModal.announcement) || getAnnouncementId(filteredAnnouncements[0]) || "",
                        instrumentId: batchModal.announcement?.instrumentId || batchModal.announcement?.assetId || batchModal.announcement?.instrument?.id || selectedInstrument?.data?.id || instrumentId || "",
                        qualificationDate: getQualificationDate(batchModal.announcement) || "",
                        dividendPerShare: batchModal.announcement?.dividendPerShare || batchModal.announcement?.amountPerShare || ""
                    }}
                    validationSchema={Yup.object({
                        announcementId: Yup.string().required("Required"),
                        instrumentId: Yup.string().required("Required"),
                        qualificationDate: Yup.string().required("Required"),
                        dividendPerShare: Yup.number().min(0).required("Required"),
                    })}
                    onSubmit={(values) => {
                        const announcementId = values.announcementId || getAnnouncementId(batchModal.announcement);
                        if (!announcementId) {
                            return;
                        }
                        generateDividendBatch({
                            announcementId,
                            instrumentId: values.instrumentId,
                            qualificationDate: values.qualificationDate,
                            dividendPerShare: Number(values.dividendPerShare),
                            userGroup: "trove",
                        })
                            .unwrap()
                            .then(() => {
                                cogoToast.success("Batch generation requested");
                                closeBatchModal();
                            })
                            .catch(() => null);
                    }}
                >
                    {({ isValid, values, setFieldValue }) => {
                        const selectedAnnouncement = filteredAnnouncements.find((announcement) => getAnnouncementId(announcement) === values.announcementId) || batchModal.announcement;
                        const selectedQualificationDate = selectedAnnouncement ? getQualificationDate(selectedAnnouncement) : "";
                        const selectedDividendPerShare = selectedAnnouncement?.dividendPerShare || selectedAnnouncement?.amountPerShare;
                        const selectedInstrumentId = selectedAnnouncement?.instrumentId || selectedAnnouncement?.assetId || selectedAnnouncement?.instrument?.id || values.instrumentId;
                        const hasAnnouncementMatch = selectedAnnouncement && values.announcementId === getAnnouncementId(selectedAnnouncement);
                        const shouldSeedDefaults = hasAnnouncementMatch && !(values.qualificationDate || values.dividendPerShare || values.instrumentId);
                        if (shouldSeedDefaults) {
                            if (selectedQualificationDate) {
                                setFieldValue("qualificationDate", selectedQualificationDate);
                            }
                            if (selectedDividendPerShare !== undefined && selectedDividendPerShare !== null) {
                                setFieldValue("dividendPerShare", selectedDividendPerShare);
                            }
                            if (selectedInstrumentId) {
                                setFieldValue("instrumentId", selectedInstrumentId);
                            }
                        }
                        return (
                            <Form>
                                <Modal.Header>
                                    <Modal.Title id="dividend-batch-modal">
                                        Generate Dividend Batch
                                    </Modal.Title>
                                    <button type="button" className="close" onClick={closeBatchModal}>
                                        <span aria-hidden="true" className="d-block">×</span>
                                        <span className="sr-only">Close</span>
                                    </button>
                                </Modal.Header>
                                <Modal.Body className="px-10 py-2">
                                    <fieldset disabled={isGeneratingBatch}>
                                        <FormLayout.Row>
                                            <TextInput
                                                label="Announcement"
                                                name="announcementId"
                                                type="text"
                                                list="dividend-announcement-options"
                                            />
                                            <datalist id="dividend-announcement-options">
                                                {filteredAnnouncements.map((announcement) => (
                                                    <option
                                                        key={getAnnouncementId(announcement)}
                                                        value={getAnnouncementId(announcement)}
                                                    >
                                                        {getInstrumentSymbol(announcement)} • {getAnnouncementId(announcement)}
                                                    </option>
                                                ))}
                                            </datalist>
                                        </FormLayout.Row>
                                        <FormLayout.Row>
                                            <TextInput
                                                label="Instrument ID"
                                                name="instrumentId"
                                                type="text"
                                            />
                                            <DateInput
                                                label="Qualification Date"
                                                name="qualificationDate"
                                            />
                                        </FormLayout.Row>
                                        <FormLayout.Row>
                                            <TextInput
                                                label="Dividend Per Share"
                                                name="dividendPerShare"
                                                type="number"
                                                step="any"
                                                min="0"
                                            />
                                        </FormLayout.Row>
                                        {!selectedAnnouncement && (
                                            <div className="text-muted small mt-2">
                                                Type an announcement ID to load its details.
                                            </div>
                                        )}
                                    </fieldset>
                                </Modal.Body>
                                <Modal.Footer>
                                    <button
                                        type="button"
                                        onClick={closeBatchModal}
                                        className="btn btn-light btn-elevate px-4"
                                        disabled={isGeneratingBatch}
                                    >
                                        Close
                                    </button>
                                    <> </>
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-elevate px-4 ml-4"
                                        disabled={isGeneratingBatch || !isValid}
                                    >
                                        Generate
                                    </button>
                                </Modal.Footer>
                            </Form>
                        );
                    }}
                </Formik>
            </Modal>
            <Modal
                show={announcementModalOpen}
                onHide={closeAnnouncementModal}
                aria-labelledby="dividend-announcement-modal"
                backdrop={isCreatingAnnouncement ? "static" : true}
                keyboard={!isCreatingAnnouncement}
            >
                <Formik
                    enableReinitialize={true}
                    initialValues={{
                        instrumentId: selectedInstrument?.data?.id || instrumentId || "",
                        symbol: selectedInstrument?.data?.symbol || "",
                        dividendPerShare: "",
                        currency: selectedInstrument?.data?.currency || "",
                        declarationDate: "",
                        qualificationDate: "",
                        paymentDate: "",
                        type: "CASH"
                    }}
                    validationSchema={Yup.object({
                        instrumentId: Yup.string().required("Required"),
                        symbol: Yup.string().required("Required"),
                        dividendPerShare: Yup.number().min(0).required("Required"),
                        currency: Yup.string().required("Required"),
                        declarationDate: Yup.date().required("Required"),
                        qualificationDate: Yup.date().required("Required"),
                        paymentDate: Yup.date().required("Required"),
                        type: Yup.string().required("Required"),
                    })}
                    onSubmit={(values, { resetForm }) => {
                        const resolvedInstrumentId = values.instrumentId || selectedInstrument?.data?.id || instrumentId;
                        if (!resolvedInstrumentId) {
                            cogoToast.error("Instrument is required");
                            return;
                        }
                        if (!values.symbol) {
                            cogoToast.error("Symbol is required");
                            return;
                        }
                        createDividendAnnouncement({ ...values, instrumentId: resolvedInstrumentId })
                            .unwrap()
                            .then(() => {
                                cogoToast.success("Dividend announcement created");
                                resetForm();
                                closeAnnouncementModal();
                            })
                            .catch(() => null);
                    }}
                >
                    {({ isValid }) => (
                        <Form>
                            <Modal.Header>
                                <Modal.Title id="dividend-announcement-modal">
                                    New Dividend Announcement
                                </Modal.Title>
                                <button type="button" className="close" onClick={closeAnnouncementModal}>
                                    <span aria-hidden="true" className="d-block">×</span>
                                    <span className="sr-only">Close</span>
                                </button>
                            </Modal.Header>
                            <Modal.Body className="px-10 py-2">
                                <fieldset disabled={isCreatingAnnouncement}>
                                    <FormLayout.Row>
                                        <TextInput
                                            label="Symbol"
                                            name="symbol"
                                            type="text"
                                        />
                                        <TextInput
                                            label="Dividend Per Share"
                                            name="dividendPerShare"
                                            type="number"
                                            step="any"
                                            min="0.0"
                                        />
                                        <TextInput
                                            label="Currency"
                                            name="currency"
                                            type="text"
                                        />
                                    </FormLayout.Row>
                                    <FormLayout.Row>
                                        <DateInput
                                            label="Declaration Date"
                                            name="declarationDate"
                                        />
                                        <DateInput
                                            label="Qualification Date"
                                            name="qualificationDate"
                                        />
                                        <DateInput
                                            label="Payment Date"
                                            name="paymentDate"
                                        />
                                    </FormLayout.Row>
                                    <FormLayout.Row>
                                        <SelectInput
                                            label="Type"
                                            name="type"
                                            options={[
                                                { value: "CASH", name: "Cash" },
                                                { value: "STOCK", name: "Stock" }
                                            ]}
                                        />
                                    </FormLayout.Row>
                                </fieldset>
                            </Modal.Body>
                            <Modal.Footer>
                                <button
                                    type="button"
                                    onClick={closeAnnouncementModal}
                                    className="btn btn-light btn-elevate px-4"
                                    disabled={isCreatingAnnouncement}
                                >
                                    Close
                                </button>
                                <> </>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-elevate px-4 ml-4"
                                    disabled={isCreatingAnnouncement || !isValid}
                                >
                                    Create Announcement
                                </button>
                            </Modal.Footer>
                        </Form>
                    )}
                </Formik>
            </Modal>
            <Modal
                show={confirmBatchAction.show}
                onHide={closeBatchActionModal}
                aria-labelledby="batch-action-confirmation-modal"
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header>
                    <Modal.Title id="batch-action-confirmation-modal">
                        Confirm {confirmBatchAction.action ? ucFirst(confirmBatchAction.action) : "Action"}
                    </Modal.Title>
                    <button type="button" className="close" onClick={closeBatchActionModal}>
                        <span aria-hidden="true" className="d-block">×</span>
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="px-10 py-2">
                    <div>
                        {confirmBatchAction.action === "approve" && "Approve this batch to enable execution."}
                        {confirmBatchAction.action === "execute" && "Execute this batch and start the payout process."}
                        {confirmBatchAction.action === "recall" && "Recall this batch after execution."}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-light btn-elevate px-4" onClick={closeBatchActionModal}>
                        {confirmBatchAction.action === "approve" && "Don't Approve"}
                        {confirmBatchAction.action === "execute" && "Don't Execute"}
                        {confirmBatchAction.action === "recall" && "Don't Recall"}
                        {!confirmBatchAction.action && "Cancel"}
                    </button>
                    <> </>
                    <button
                        type="button"
                        className="btn btn-primary btn-elevate px-4 ml-4"
                        disabled={
                            !selectedBatchId ||
                            isBatchLoading ||
                            (confirmBatchAction.action === "approve" && isApprovingBatch) ||
                            (confirmBatchAction.action === "execute" && isExecutingBatch) ||
                            (confirmBatchAction.action === "recall" && isRecallingBatch)
                        }
                        onClick={() => {
                            const action = confirmBatchAction.action;
                            const actionMap = {
                                approve: approveDividendBatch,
                                execute: executeDividendBatch,
                                recall: recallDividendBatch,
                            };
                            const handler = actionMap[action];
                            if (!handler || !selectedBatchId) {
                                closeBatchActionModal();
                                return;
                            }
                            handler(selectedBatchId)
                                .unwrap()
                                .then(() => {
                                    if (action === "approve") {
                                        cogoToast.success("Batch approved");
                                    } else if (action === "execute") {
                                        cogoToast.success("Batch execution started");
                                    } else if (action === "recall") {
                                        cogoToast.success("Batch recall requested");
                                    }
                                    closeBatchActionModal();
                                })
                                .catch(() => null);
                        }}
                    >
                        Confirm
                    </button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={auditModal.show}
                onHide={closeAuditModal}
                aria-labelledby="batch-audit-modal"
                size="lg"
                centered
            >
                <Modal.Header>
                    <Modal.Title id="batch-audit-modal">
                        Audit Trail {auditModal.referenceId ? `• ${auditModal.referenceId}` : ""}
                    </Modal.Title>
                    <button type="button" className="close" onClick={closeAuditModal}>
                        <span aria-hidden="true" className="d-block">×</span>
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="px-10 py-2">
                    {auditRecords.length === 0 && (
                        <div className="text-muted">No audit entries available.</div>
                    )}
                    <div className="d-flex flex-column">
                        {auditRecords.map((entry) => (
                            <div key={entry.id || entry.autoId || entry.referenceId} className="border rounded p-4 mb-4">
                                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center flex-wrap">
                                        <span className="badge badge-light-primary mr-3">{formatAuditAction(entry.action)}</span>
                                        <div className="text-muted mr-4">Admin: {entry.adminId || entry.admin || "-"}</div>
                                        <div className="text-muted">User: {getAuditUserLabel(entry)}</div>
                                    </div>
                                    <div className="text-muted">{formatDate(entry.created || entry.timestamp)}</div>
                                </div>
                                {(entry.recordId || entry.batchId) && (
                                    <div className="text-muted mb-3">
                                        {entry.recordId && <span className="mr-4">Record: {entry.recordId}</span>}
                                        {entry.batchId && <span>Batch: {entry.batchId}</span>}
                                    </div>
                                )}
                                {(entry.reason || entry.adjustmentReason) && (
                                    <div className="mb-3">
                                        <strong>Reason:</strong> {entry.reason || entry.adjustmentReason}
                                    </div>
                                )}
                                <div className="row">
                                    <div className="col-lg-6 mb-3">
                                        <div className="text-muted mb-2">Old Value</div>
                                        {renderAuditValue(entry.oldValue)}
                                    </div>
                                    <div className="col-lg-6 mb-3">
                                        <div className="text-muted mb-2">New Value</div>
                                        {renderAuditValue(entry.newValue)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-light btn-elevate px-4" onClick={closeAuditModal}>
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
